import React from 'react';
import {
  NormalizedGaushala,
  CONFIDENCE_LEVELS,
} from '../../types/gaushalaMap.types';
import DataSourceIndicator from './DataSourceIndicator';

/**
 * Marker Popup Component
 *
 * Displays detailed information about a gaushala when marker is clicked:
 * - Name and address
 * - Location coordinates
 * - Cattle statistics
 * - Status
 * - Data source indicator
 */

interface MarkerPopupProps {
  gaushala: NormalizedGaushala;
  onClose?: () => void;
}

const MarkerPopup: React.FC<MarkerPopupProps> = ({ gaushala, onClose }) => {
  const confidenceInfo =
    CONFIDENCE_LEVELS[
      gaushala.confidence as keyof typeof CONFIDENCE_LEVELS
    ];

  return (
    <div className="gaushala-popup-content text-xs">
      {/* Header with confidence badge */}
      <div className="mb-2 pb-2 border-b border-gray-200">
        <div className="flex items-start justify-between gap-1">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-800 text-sm leading-tight">
              {gaushala.name}
            </h3>
          </div>
          <span
            className="px-1.5 py-0.5 rounded text-xs font-semibold text-white whitespace-nowrap flex-shrink-0"
            style={{ backgroundColor: confidenceInfo.color }}
          >
            {confidenceInfo.label}
          </span>
        </div>
      </div>

      {/* Location Source Badge - More Compact */}
      <div className="mb-2">
        {gaushala.coordinateSource === 'geocoded_from_api' ? (
          <p className="text-xs font-semibold text-green-600">
            ✅ EXACT Location (Geocoded)
          </p>
        ) : gaushala.coordinateSource === 'user_provided' ? (
          <p className="text-xs font-semibold text-blue-600">
            🎯 User Provided
          </p>
        ) : gaushala.coordinateSource === 'failed' ? (
          <p className="text-xs font-semibold text-red-600">
            ⚠️ Failed
          </p>
        ) : (
          <p className="text-xs font-semibold text-gray-600">
            ⏳ Pending
          </p>
        )}
      </div>

      {/* Address Info - Compact */}
      <div className="mb-2 pb-2 border-b border-gray-200">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-0.5">
          Location
        </p>
        <p className="text-xs text-gray-800 break-words leading-snug">
          {gaushala.address}
        </p>
        {gaushala.pincode && (
          <p className="text-xs text-gray-600 mt-0.5">
            📮 {gaushala.pincode}
          </p>
        )}
        {/* Exact Coordinates - Compact */}
        <div className="mt-1 text-xs text-gray-600 font-mono bg-gray-50 p-1 rounded border border-gray-200">
          <p className="font-semibold text-gray-700">Coordinates:</p>
          <p className="text-gray-700 leading-tight">
            {gaushala.latitude.toFixed(6)}°N, {gaushala.longitude.toFixed(6)}°E
          </p>
        </div>
      </div>

      {/* Cattle Statistics - Improved Layout */}
      <div className="mb-2 pb-2 border-b border-gray-200">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
          Cattle Info
        </p>
        <div className="space-y-1">
          <div className="bg-blue-50 p-1.5 rounded">
            <p className="text-xs text-gray-600">Total Cattle</p>
            <p className="text-sm font-bold text-blue-600">
              {gaushala.metadata.totalCattle ?? 'Not provided'}
            </p>
          </div>
          <div className="bg-green-50 p-1.5 rounded">
            <p className="text-xs text-gray-600">Milking Cows</p>
            <p className="text-sm font-bold text-green-600">
              {gaushala.metadata.milkingCows ?? 'Not provided'}
            </p>
          </div>
          {gaushala.metadata.totalLandArea && (
            <div className="bg-amber-50 p-1.5 rounded">
              <p className="text-xs text-gray-600">Land Area</p>
              <p className="text-xs font-semibold text-amber-700">
                {gaushala.metadata.totalLandArea} acres
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Status and Data Source - Compact */}
      <div className="mb-2 pb-2 border-b border-gray-200">
        <div className="space-y-1">
          <div>
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-0.5">
              Status
            </p>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                gaushala.metadata.status === 'ACTIVE'
                  ? 'bg-green-100 text-green-800'
                  : gaushala.metadata.status === 'INACTIVE'
                    ? 'bg-gray-100 text-gray-800'
                    : 'bg-red-100 text-red-800'
              }`}
            >
              {gaushala.metadata.status}
            </span>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-0.5">
              Source
            </p>
            <DataSourceIndicator
              dataSource={gaushala.dataSource}
              confidence={gaushala.confidence}
              showLabel={true}
            />
          </div>
        </div>
      </div>

      {/* Date */}
      <div className="mb-2 text-xs text-gray-500">
        📅 {new Date(gaushala.metadata.createdAt).toLocaleDateString()}
      </div>

      {/* Data Quality Notice - Compact */}
      {gaushala.dataSource !== 'database' && (
        <div className="mb-2 p-1 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
          <p className="font-semibold mb-0.5">ℹ️ Auto-detected location</p>
          <p className="leading-tight">Verify with gaushala for accuracy</p>
        </div>
      )}

      {/* Close Button */}
      {onClose && (
        <button
          onClick={onClose}
          className="w-full px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs font-semibold rounded transition-colors"
        >
          Close
        </button>
      )}
    </div>
  );
};

export default MarkerPopup;
